/**
 * Lightweight UX event tracking: buffer + batch flush (8–10s, visibilitychange, pagehide).
 * Only runs when UX_TRACKING_ENABLED is true (see initUXTracking).
 * Uses sendBeacon or fetch(keepalive); never blocks the main thread.
 */

import { getUxFlags } from "./uxFlags";

const FLUSH_INTERVAL_MS_MIN = 8000;
const FLUSH_INTERVAL_MS_MAX = 10000;

type UxEventType =
  | "page_view"
  | "click"
  | "scroll_depth"
  | "time_on_page"
  | "time_on_section"
  | "cta_hover_time"
  | "cta_variant_shown"
  | "cta_variant_click"
  | "cta_variant_conversion_start"
  | "cta_variant_conversion_complete"
  | "layout_variant_shown"
  | "services_variant_shown"
  | "service_card_click"
  | "faq_variant_shown"
  | "faq_item_open"
  | "goal_start"
  | "goal_complete"
  | "whatsapp_click"
  | "form_submit_start"
  | "form_submit_complete"
  | "chat_handover_start"
  | "chat_handover_complete"
  | "nudge_shown"
  | "nudge_click"
  | "contact_method_selected"
  | "contact_detail_submitted"
  | "consent_given"
  | "consent_declined";

interface UxEventPayload {
  type: UxEventType;
  value?: number | string;
  section_id?: string;
  ts?: number;
  flow_id?: string;
  meta?: Record<string, string | number | boolean>;
}

/** Separate goal flow ID key — distinct from CTA conversion flow. */
const STORAGE_GOAL_FLOW_ID_KEY = "ux_goal_flow_id";
/** Step 13: policy source set when adaptive policy is applied. */
const STORAGE_VB_POLICY_SOURCE_KEY = "ux_vb_policy_source";

export const UX_SESSION_STORAGE_KEY = "ux_session_id";
const STORAGE_SESSION_ID = UX_SESSION_STORAGE_KEY;
const STORAGE_ANON_USER_ID = "ux_anon_user_id";
const STORAGE_LAST_CTA_VARIANT_KEY = "ux_last_cta_variant";
const STORAGE_FLOW_ID_KEY = "ux_flow_id";

function uuidV4(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrSetSessionId(): string {
  try {
    let id = sessionStorage.getItem(STORAGE_SESSION_ID);
    if (!id) {
      id = uuidV4();
      sessionStorage.setItem(STORAGE_SESSION_ID, id);
    }
    return id;
  } catch {
    return uuidV4();
  }
}

function getOrSetAnonUserId(): string {
  try {
    let id = localStorage.getItem(STORAGE_ANON_USER_ID);
    if (!id) {
      id = uuidV4();
      localStorage.setItem(STORAGE_ANON_USER_ID, id);
    }
    return id;
  } catch {
    return uuidV4();
  }
}

function getDeviceType(): "mobile" | "desktop" | "tablet" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) return "mobile";
  if (/ipad|tablet|playbook|silk/i.test(ua)) return "tablet";
  return "desktop";
}

let buffer: UxEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
let pageStartTime = 0;

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  const delay = FLUSH_INTERVAL_MS_MIN + Math.random() * (FLUSH_INTERVAL_MS_MAX - FLUSH_INTERVAL_MS_MIN);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, delay);
}

function buildBatch(): { session_id: string; anon_user_id: string; timestamp: number; path: string; referrer: string; locale: string; timezone_offset: number; device_type: string; events: UxEventPayload[] } | null {
  if (buffer.length === 0) return null;
  const path = typeof location !== "undefined" ? location.pathname + location.search : "/";
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  const locale = typeof navigator !== "undefined" ? (navigator.language || navigator.languages?.[0] || "en") : "en";
  const timezone_offset = typeof Intl !== "undefined" ? -new Date().getTimezoneOffset() : 0;
  const events = buffer.slice();
  buffer = [];
  return {
    session_id: getOrSetSessionId(),
    anon_user_id: getOrSetAnonUserId(),
    timestamp: Date.now(),
    path,
    referrer,
    locale,
    timezone_offset,
    device_type: getDeviceType(),
    events,
  };
}

function flush(): void {
  const batch = buildBatch();
  if (!batch) return;
  const url = "/api/ux-events";
  const body = JSON.stringify(batch);
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } catch {
      fetch(url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
    }
  } else {
    fetch(url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
  }
}

function push(event: UxEventPayload): void {
  if (!initialized) return;
  buffer.push({ ...event, ts: event.ts ?? Date.now() });
  scheduleFlush();
}

function installVisibilityFlush(): void {
  if (typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (pageStartTime > 0) push({ type: "time_on_page", value: Date.now() - pageStartTime });
      flush();
    }
  });
  window.addEventListener("pagehide", () => {
    if (pageStartTime > 0) push({ type: "time_on_page", value: Date.now() - pageStartTime });
    flush();
  });
}

// --- Public API (no-op if tracking disabled) ---

export function trackPageView(): void {
  push({ type: "page_view" });
}

export function trackClick(value?: string): void {
  push({ type: "click", value });
}

export function trackScrollDepth(percent: 25 | 50 | 75 | 100): void {
  push({ type: "scroll_depth", value: percent });
}

export function trackTimeOnPage(ms: number): void {
  push({ type: "time_on_page", value: ms });
}

export function trackTimeOnSection(sectionId: string, ms: number): void {
  push({ type: "time_on_section", section_id: sectionId, value: ms });
}

export function trackCtaHoverTime(ms: number): void {
  push({ type: "cta_hover_time", value: ms });
}

/** Cache: max 1 cta_variant_shown par (pathname, variant) par page view. */
let ctaShownPathname: string | null = null;
let ctaShownVariants: Set<string> = new Set();

export function trackCtaVariantShown(pathname: string, variant: string): void {
  if (pathname !== ctaShownPathname) {
    ctaShownPathname = pathname;
    ctaShownVariants = new Set();
  }
  if (ctaShownVariants.has(variant)) return;
  ctaShownVariants.add(variant);
  push({ type: "cta_variant_shown", value: variant });
}

export function trackCtaVariantClick(variant: string): void {
  push({ type: "cta_variant_click", value: variant });
}

export function trackCtaVariantConversionStart(variant?: string): void {
  const v = variant ?? "";
  try {
    if (typeof sessionStorage !== "undefined" && v) sessionStorage.setItem(STORAGE_LAST_CTA_VARIANT_KEY, v);
  } catch {}
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") {
      flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
      if (!flowId) {
        flowId = uuidV4();
        sessionStorage.setItem(STORAGE_FLOW_ID_KEY, flowId);
      }
    }
  } catch {}
  push({
    type: "cta_variant_conversion_start",
    value: v,
    ...(flowId ? { flow_id: flowId } : {}),
  });
}

export function trackLayoutVariantShown(homeVariant: string): void {
  const normalized = homeVariant === "price_first" ? "action_first" : homeVariant;
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
  } catch {}
  push({
    type: "layout_variant_shown",
    value: normalized,
    ...(flowId ? { flow_id: flowId } : {}),
  });
}

/** Cache: au plus 1 services_variant_shown par session (reset on page reload). */
let servicesShownTracked = false;

export function trackServicesVariantShown(order: string[]): void {
  if (servicesShownTracked) return;
  servicesShownTracked = true;
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
  } catch {}
  push({
    type: "services_variant_shown",
    value: JSON.stringify(order),
    ...(flowId ? { flow_id: flowId } : {}),
  });
}

/** Cache: au plus 1 faq_variant_shown par session (reset on page reload). */
let faqShownTracked = false;

export function trackFaqVariantShown(variant: string): void {
  if (faqShownTracked) return;
  faqShownTracked = true;
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
  } catch {}
  push({
    type: "faq_variant_shown",
    value: variant,
    ...(flowId ? { flow_id: flowId } : {}),
  });
}

export function trackFaqItemOpen(questionId: string): void {
  push({ type: "faq_item_open", value: questionId });
}

const STORAGE_SERVICE_INTENT_KEY = "ux_service_intent";

export function trackServiceCardClick(serviceId: string): void {
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
  } catch {}
  push({
    type: "service_card_click",
    value: serviceId,
    ...(flowId ? { flow_id: flowId } : {}),
  });
  // DEV: persist serviceIntent in sessionStorage for overlay / debug tools
  if (typeof import.meta !== "undefined" && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(STORAGE_SERVICE_INTENT_KEY, serviceId);
      }
    } catch {}
  }
}

export function trackCtaVariantConversionComplete(variant?: string): void {
  let v = variant;
  if (v === undefined || v === "") {
    try {
      if (typeof sessionStorage !== "undefined") v = sessionStorage.getItem(STORAGE_LAST_CTA_VARIANT_KEY) ?? "";
    } catch {
      v = "";
    }
  }
  let flowId: string | null = null;
  try {
    if (typeof sessionStorage !== "undefined") {
      flowId = sessionStorage.getItem(STORAGE_FLOW_ID_KEY);
      if (flowId) sessionStorage.removeItem(STORAGE_FLOW_ID_KEY);
    }
  } catch {}
  push({
    type: "cta_variant_conversion_complete",
    value: v ?? "",
    ...(flowId ? { flow_id: flowId } : {}),
  });
}

// ─── Step 13: VariantBundle attribution ──────────────────────────────────────

/**
 * Reads the current UX variant context from DOM attributes and sessionStorage.
 * Returns a flat `vb_*` record safe to include in goal event meta.
 * Only includes fields that have a non-empty value.
 */
export function getVariantBundleFromSession(): Record<string, string> {
  const bundle: Record<string, string> = {};
  try {
    // vb_cta: last adaptive CTA variant shown to this session
    const cta = typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(STORAGE_LAST_CTA_VARIANT_KEY) : null;
    if (cta) bundle.vb_cta = cta;

    if (typeof document !== "undefined") {
      // vb_home: home layout variant (set on <main data-ux-home-variant="...">)
      const homeEl = document.querySelector("[data-ux-home-variant]");
      const homeV = homeEl?.getAttribute("data-ux-home-variant");
      if (homeV) bundle.vb_home = homeV;

      // vb_services: spotlight service ID (card with data-ux-spotlight="true")
      const spotlightEl = document.querySelector("[data-ux-spotlight='true']");
      const spotId = spotlightEl?.getAttribute("data-testid")?.replace("card-service-", "");
      if (spotId) bundle.vb_services = spotId;

      // vb_faq: FAQ variant (set on <section data-ux-faq-variant="...">)
      const faqEl = document.querySelector("[data-ux-faq-variant]");
      const faqV = faqEl?.getAttribute("data-ux-faq-variant");
      if (faqV) bundle.vb_faq = faqV;
    }

    // vb_intent: explicit service interest clicked in this session
    const intent = typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(STORAGE_SERVICE_INTENT_KEY) : null;
    if (intent) bundle.vb_intent = intent;

    // vb_source: policy source (openclaw | deterministic) if set by hook
    const source = typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(STORAGE_VB_POLICY_SOURCE_KEY) : null;
    if (source) bundle.vb_source = source;
  } catch {
    // DOM or sessionStorage not available (SSR, etc.)
  }
  return bundle;
}

/**
 * Called by adaptive hooks (e.g. usePredictiveUx) when a policy response is received.
 * Persists the policy source for attribution in subsequent goal events.
 */
export function setPolicySource(source: string): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(STORAGE_VB_POLICY_SOURCE_KEY, source);
    }
  } catch {}
}

// ─── Step 9+10: Conversion Graph helpers ─────────────────────────────────────

function getOrCreateGoalFlowId(): string {
  try {
    if (typeof sessionStorage !== "undefined") {
      let id = sessionStorage.getItem(STORAGE_GOAL_FLOW_ID_KEY);
      if (!id) {
        id = uuidV4();
        sessionStorage.setItem(STORAGE_GOAL_FLOW_ID_KEY, id);
      }
      return id;
    }
  } catch {}
  return uuidV4();
}

function clearGoalFlowId(): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(STORAGE_GOAL_FLOW_ID_KEY);
    }
  } catch {}
}

/**
 * Track the start of a conversion goal.
 * Auto-attaches the current variant bundle from DOM/sessionStorage if not explicitly provided.
 */
export function trackGoalStart(goalId: string, explicitBundle?: Record<string, string>): void {
  const flowId = getOrCreateGoalFlowId();
  const bundle = explicitBundle ?? getVariantBundleFromSession();
  const meta = Object.keys(bundle).length > 0 ? bundle : undefined;
  push({
    type: "goal_start",
    value: goalId,
    flow_id: flowId,
    ...(meta ? { meta } : {}),
  });
}

/**
 * Track completion of a conversion goal. Clears the goal flow_id.
 * Auto-attaches the current variant bundle from DOM/sessionStorage if not explicitly provided.
 */
export function trackGoalComplete(goalId: string, explicitBundle?: Record<string, string>): void {
  const flowId = getOrCreateGoalFlowId();
  const bundle = explicitBundle ?? getVariantBundleFromSession();
  const meta = Object.keys(bundle).length > 0 ? bundle : undefined;
  push({
    type: "goal_complete",
    value: goalId,
    flow_id: flowId,
    ...(meta ? { meta } : {}),
  });
  clearGoalFlowId();
}

export function trackWhatsappClick(): void {
  const flowId = getOrCreateGoalFlowId();
  push({ type: "whatsapp_click", flow_id: flowId });
}

export function trackFormSubmitStart(): void {
  push({ type: "form_submit_start", flow_id: getOrCreateGoalFlowId() });
}

export function trackFormSubmitComplete(): void {
  const flowId = getOrCreateGoalFlowId();
  push({ type: "form_submit_complete", flow_id: flowId });
  clearGoalFlowId();
}

export function trackChatHandoverStart(): void {
  push({ type: "chat_handover_start", flow_id: getOrCreateGoalFlowId() });
}

export function trackChatHandoverComplete(): void {
  const flowId = getOrCreateGoalFlowId();
  push({ type: "chat_handover_complete", flow_id: flowId });
  clearGoalFlowId();
}

export function trackNudgeShown(nudgeId: string): void {
  push({ type: "nudge_shown", value: nudgeId });
}

export function trackNudgeClick(nudgeId: string): void {
  push({ type: "nudge_click", value: nudgeId });
}

export function trackContactMethodSelected(contactMethod: string): void {
  push({ type: "contact_method_selected", value: contactMethod });
}

export function trackContactDetailSubmitted(contactMethod: string): void {
  push({ type: "contact_detail_submitted", meta: { contact_method: contactMethod } });
}

export function trackConsentGiven(): void {
  push({ type: "consent_given" });
}

export function trackConsentDeclined(): void {
  push({ type: "consent_declined" });
}

/** Call once after app mount. Fetches flags; if UX_TRACKING_ENABLED, starts tracking and records page_view. */
export async function initUXTracking(): Promise<void> {
  const flags = await getUxFlags();
  if (!flags.uxTrackingEnabled) return;
  initialized = true;
  getOrSetSessionId();
  pageStartTime = Date.now();
  installVisibilityFlush();
  trackPageView();
  if (typeof document !== "undefined") {
    document.addEventListener(
      "click",
      (e) => {
        const t = e.target as HTMLElement;
        const value = t?.id || (t?.getAttribute?.("data-ux-cta") as string) || undefined;
        trackClick(value);
      },
      { passive: true, capture: true }
    );
  }
  // Scroll depth: single listener, fire once per threshold
  if (typeof document !== "undefined") {
    const thresholds = [25, 50, 75, 100];
    const fired = new Set<number>();
    const onScroll = () => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = (document.documentElement.scrollHeight || document.body.scrollHeight) - window.innerHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);
      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackScrollDepth(t as 25 | 50 | 75 | 100);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  }
}
